import React, {useContext, useMemo, useRef, useState} from "react"
import AppContext from "../../appContext"
import LimeProgramLabInputRow from "../../controllers/LimeProgramLabInputRow"
import LimeProgramLabInputs from "../../controllers/LimeProgramLabInputs"
import LabInputUnit from "../../controllers/LabInputUnit"
import SampleDepth from "../../controllers/SampleDepth"
import NullableMeasureValue from "../../controllers/NullableMeasureValue"
import {useBoundaries} from "../../controllers/useBoundaries"
import {boundariesToRings} from "../../controllers/boundaryGeo"
import {DataTable} from "../../components/DataTable"
import {PlusCircleIcon, ArrowUpOnSquareIcon, MapIcon} from "@heroicons/react/24/solid"
import {arrayPush, arrayRemoveIndex, arrayUpdate} from "../../immutableState"
import Dialog from "../../components/Dialog"
import FileUploader from "../../components/FileUploader"
import SampleLocationsDialog from "./SampleLocationsDialog"
import {parseCSVFile} from "../program/CSVParse"
import {customUnits, unitCustom} from "../program/CustomUnits"
import {getValueNullable} from "../program/Helpers"
import {NullableMeasureValueField} from "../program/MeasureValueField"
import NumberNullable from "../../components/NumberNullable"
import Number from "../../components/Number"
import {onInputHandler} from "../../wrapper"
import {KeyOfType} from "../../components/Fields"
import {
    RowWithId,
    createEmptyRow,
    isMeq100g,
    formatValue,
    formatMeasureElement,
    convertRowForEditing,
    convertRowForSaving,
    buildLimeSoilColumnMatchers,
    rowsToSamplePoints
} from "./limeSoilUtils"

interface LimeSoilDataDialogProps {
    data: LimeProgramLabInputs
    onSave: (data: LimeProgramLabInputs) => void
    onDiscard: () => void
    labInputUnit?: LabInputUnit
    landIds?: number[]
}

let nextId = -1
function getNextId(): number {
    return nextId--
}

const LimeSoilDataDialog: React.FC<LimeSoilDataDialogProps> = (props) => {
    const app = useContext(AppContext)
    const system = app.initial.system
    const [data, setData] = useState<RowWithId[]>(() =>
        props.data.rows.map((row, index) => ({...row, _id: index}))
    )
    const [showUpload, setShowUpload] = useState(false)
    const [showEdit, setShowEdit] = useState(false)
    const [editRow, setEditRow] = useState<RowWithId>(createEmptyRow(0))
    const [editIndex, setEditIndex] = useState<number>(-1)
    const changed = useRef<boolean>(false)
    const [showMap, setShowMap] = useState(false)

    // Use the useBoundary hook instead of manual useEffect
    const {geometryMap, loading: boundaryLoading, error: boundaryError} = useBoundaries(props.landIds)
    const multipleBoundaries = React.useMemo(() => boundariesToRings(Object.values(geometryMap)), [geometryMap])

    // Memoize sample points to avoid recalculating on every render
    const samplePoints = useMemo(() => rowsToSamplePoints(data), [data])

    // meq/100g regions use different unit for K, Na, Ca, Mg; other regions use mg/kg
    const meq100gMode = isMeq100g(props.labInputUnit)

    // Get the unit label - meq/100g regions show meq/100g, others show mg/kg or ppm
    const unitLabel = meq100gMode ? 'meq/100g' : unitCustom(system, customUnits.mgKg_ppm)

    // Helper for NullableMeasureValue fields (follows EditSoil pattern)
    function nullableMeasure(label: string, key: KeyOfType<RowWithId, NullableMeasureValue>) {
        return <td>
            <div>{label}</div>
            <NullableMeasureValueField
                unit={customUnits.mgKg_ppm}
                value={editRow[key]}
                update={v => setEditRow({...editRow, [key]: v})}
            />
        </td>
    }

    // Helper for nullable number fields
    function nullableNumber(label: string, key: KeyOfType<RowWithId, number | null>) {
        return <td>
            <div>{label}</div>
            <NumberNullable value={editRow[key]} change={v => setEditRow({...editRow, [key]: v})}/>
        </td>
    }

    function handleCsvUpload(file: File) {
        const reader = new FileReader()
        reader.onload = (e) => {
            const text = e.target?.result as string
            const columnMatchers = buildLimeSoilColumnMatchers(meq100gMode)
            const rows = parseCSVFile<RowWithId>(
                text,
                () => createEmptyRow(getNextId()),
                system,
                columnMatchers
            )
            setData(prev => [...prev, ...rows])
            changed.current = true
            setShowUpload(false)
        }
        reader.readAsText(file)
    }

    function addRow() {
        setEditRow(createEmptyRow(getNextId()))
        setEditIndex(-1)
        setShowEdit(true)
    }

    function onEdit(row: RowWithId, index: number) {
        setEditRow(convertRowForEditing(row, meq100gMode))
        setEditIndex(index)
        setShowEdit(true)
    }

    function onRemove(index: number) {
        setData(arrayRemoveIndex(data, index))
        changed.current = true
    }

    function saveEdit() {
        const rowToSave = convertRowForSaving(editRow, meq100gMode)

        if (editIndex === -1) {
            setData(arrayPush(data, rowToSave))
        } else {
            setData(arrayUpdate(data, editIndex, rowToSave))
        }
        changed.current = true
        setShowEdit(false)
    }

    function formatMeasure(value: NullableMeasureValue): string {
        const v = getValueNullable(system, value)
        if (v === null) return '-'
        return v.toFixed(2)
    }

    return <>
        <Dialog
            title={app.word('upload_csv')}
            show={showUpload}
            setShow={setShowUpload}
            body={
                <div className="p-4">
                    <div>{app.word('download_csv_sample')}, {app.word('add_data_and_upload_again')}.</div>
                    <a className="underline" href="/LimeSoilSample.csv" download="LimeSoilSample.csv">LimeSoilSample.csv</a>
                    <div className="my-2">
                        <FileUploader onChange={file => handleCsvUpload(file)} fileTypes=".csv"/>
                    </div>
                </div>
            }
        />

        <Dialog
            title={editIndex === -1 ? app.word('add') : app.word('edit')}
            show={showEdit}
            setShow={setShowEdit}
            body={
                <div className="p-4 max-h-[70vh] overflow-y-auto">
                    <table className="w-full">
                        <tbody>
                            <tr>
                                <td>
                                    <div>{app.word('block')}</div>
                                    <div className="input bg-gray-100">{editRow.landName || '—'}</div>
                                </td>
                                <td>
                                    <div>{app.word('reference_nr')}</div>
                                    <input className="input" type="text" value={editRow.referenceNr}
                                           onInput={onInputHandler(v => setEditRow({...editRow, referenceNr: v}))}/>
                                </td>
                                {nullableNumber(app.word('latitude'), 'latitude')}
                                {nullableNumber(app.word('longitude'), 'longitude')}
                                <td>
                                    <div>{app.word('sample_depth')}</div>
                                    <select
                                        className="input"
                                        value={editRow.sampleDepth}
                                        onChange={e => setEditRow({...editRow, sampleDepth: e.target.value as SampleDepth})}
                                    >
                                        <option value={SampleDepth.TopSoil}>{app.word(SampleDepth.TopSoil)}</option>
                                        <option value={SampleDepth.SubSoil}>{app.word(SampleDepth.SubSoil)}</option>
                                        <option value={SampleDepth.TopSoilMicros}>{app.word(SampleDepth.TopSoilMicros)}</option>
                                    </select>
                                </td>
                                <td>
                                    <div>pH(H2O)</div>
                                    <Number value={editRow.phH2O} change={v => setEditRow({...editRow, phH2O: v})}/>
                                </td>
                            </tr>

                            <tr>
                                {nullableMeasure('P Bray I', 'pBrayI')}
                                {nullableMeasure(`K (${unitLabel})`, 'k')}
                                {nullableMeasure(`Na (${unitLabel})`, 'na')}
                                {nullableMeasure(`Ca (${unitLabel})`, 'ca')}
                                {nullableMeasure(`Mg (${unitLabel})`, 'mg')}
                                <td>
                                    <div>Exch H+</div>
                                    <Number value={editRow.exchH} change={v => setEditRow({...editRow, exchH: v})}/>
                                </td>
                            </tr>

                            <tr>
                                {nullableMeasure(app.word('sulfur'), 'sulfur')}
                                <td>
                                    <div>{app.word('density')}</div>
                                    <Number value={editRow.density} change={v => setEditRow({...editRow, density: v})}/>
                                </td>
                                {nullableNumber('%Ca', 'caPercent')}
                                {nullableNumber('%Mg', 'mgPercent')}
                                {nullableNumber('%K', 'kPercent')}
                                {nullableNumber('%Na', 'naPercent')}
                            </tr>

                            <tr>
                                {nullableNumber(app.word('acid_saturated'), 'acidSat')}
                                {nullableNumber('Ca:Mg', 'caMgRatio')}
                                {nullableNumber('(Ca+Mg)/K', 'caMgKRatio')}
                                {nullableNumber('Mg:K', 'mgKRatio')}
                                {nullableNumber('S-Value', 'sValue')}
                                {nullableNumber('Na:K', 'naKRatio')}
                            </tr>

                            <tr>
                                {nullableNumber('CEC', 'cec')}
                            </tr>
                        </tbody>
                    </table>

                    <div className="flex justify-end items-end">
                        <button className="btn-sm bg-red-500" onClick={() => setShowEdit(false)}>
                            {app.word('discard')}
                        </button>
                        <button className="btn-sm bg-primary" onClick={saveEdit}>
                            {app.word('save')}
                        </button>
                    </div>
                </div>
            }
        />

        <SampleLocationsDialog
            show={showMap}
            setShow={setShowMap}
            rows={data}
            samplePoints={samplePoints}
            multipleBoundaries={multipleBoundaries}
            boundaryLoading={boundaryLoading}
            boundaryError={boundaryError}
            meq100gMode={meq100gMode}
        />

        <div className="my-2">
            {data.length > 0 ? (
                <>
                    <div className="text-xl text-primary mb-2">{app.word('soil_data')}</div>
                    <div className="overflow-x-auto">
                        <DataTable
                            headerClass="px-2 py-2 text-left text-xs font-semibold bg-gray-200"
                            dataClass="whitespace-nowrap px-1 py-2 text-xs text-gray-600"
                            keyExtractor={(row: RowWithId) => row._id}
                            definitions={[
                                {header: app.word('block'), row: r => r.landName || '—'},
                                {header: app.word('reference_nr'), row: r => r.referenceNr},
                                {header: app.word('latitude'), row: r => formatValue(r.latitude)},
                                {header: app.word('longitude'), row: r => formatValue(r.longitude)},
                                {header: app.word('sample_depth'), row: r => app.word(r.sampleDepth)},
                                {header: 'pH(H2O)', row: r => formatValue(r.phH2O)},
                                {header: `P Bray I`, row: r => formatMeasure(r.pBrayI)},
                                {header: `K (${unitLabel})`, row: r => formatMeasureElement(system, r.k, 'K', meq100gMode)},
                                {header: `Na (${unitLabel})`, row: r => formatMeasureElement(system, r.na, 'Na', meq100gMode)},
                                {header: `Ca (${unitLabel})`, row: r => formatMeasureElement(system, r.ca, 'Ca', meq100gMode)},
                                {header: `Mg (${unitLabel})`, row: r => formatMeasureElement(system, r.mg, 'Mg', meq100gMode)},
                                {header: 'Exch H', row: r => formatValue(r.exchH)},
                                {header: '%Ca', row: r => formatValue(r.caPercent)},
                                {header: '%Mg', row: r => formatValue(r.mgPercent)},
                                {header: '%K', row: r => formatValue(r.kPercent)},
                                {header: '%Na', row: r => formatValue(r.naPercent)},
                                {header: app.word('acid_saturated'), row: r => formatValue(r.acidSat)},
                                {header: 'Ca:Mg', row: r => formatValue(r.caMgRatio)},
                                {header: '(Ca+Mg)/K', row: r => formatValue(r.caMgKRatio)},
                                {header: 'Mg:K', row: r => formatValue(r.mgKRatio)},
                                {header: 'S-Value', row: r => formatValue(r.sValue)},
                                {header: 'Na:K', row: r => formatValue(r.naKRatio)},
                                {header: 'CEC', row: r => formatValue(r.cec)},
                                {header: app.word('density'), row: r => formatValue(r.density)},
                                {header: `${app.word('sulfur')} (${unitLabel})`, row: r => formatMeasure(r.sulfur)},
                                {
                                    header: app.word('actions'),
                                    row: (r, index) => <div>
                                        <span className="underline cursor-pointer text-primary"
                                              onClick={() => onRemove(index)}>{app.word('remove')}</span>
                                        <span className="px-2">|</span>
                                        <span className="underline cursor-pointer text-primary"
                                              onClick={() => onEdit(r, index)}>{app.word('edit')}</span>
                                    </div>
                                }
                            ]}
                            data={data}
                        />
                    </div>
                </>
            ) : (
                <div>{app.word('no_soil_data')}</div>
            )}

            <div className="flex items-center mt-2">
                <span title={app.word('add')} className="mr-2">
                    <PlusCircleIcon className="w-6 h-6 cursor-pointer" onClick={addRow}/>
                </span>
                <span title={app.word('upload_csv')} className="mr-2">
                    <ArrowUpOnSquareIcon className="w-6 h-6 cursor-pointer" onClick={() => setShowUpload(true)}/>
                </span>
                {(samplePoints.length > 0 || multipleBoundaries.length > 0 || (props.landIds && props.landIds.length > 0)) && (
                    <span title={app.word('view_map')}>
                        <MapIcon
                            className={`w-6 h-6 cursor-pointer ${boundaryLoading ? 'text-gray-400 animate-pulse' : 'text-primary'}`}
                            onClick={() => setShowMap(true)}
                        />
                    </span>
                )}
            </div>
        </div>

        <div className="border-t mt-4 pt-4">
            <div className="flex justify-end">
                <button className="btn-sm bg-red-500" onClick={() => props.onDiscard()}>
                    {app.word('discard')}
                </button>
                <button className="btn-sm bg-primary" onClick={() => {
                    if (changed.current) {
                        // Strip out the _id field before saving
                        const rowsWithoutId: LimeProgramLabInputRow[] = data.map(({_id, ...row}) => row)
                        props.onSave({rows: rowsWithoutId})
                    } else {
                        props.onDiscard()
                    }
                }}>
                    {app.word('save')}
                </button>
            </div>
        </div>
    </>
}

export default LimeSoilDataDialog
